// One player's platform record (spec/14 §10 rule 2, F-HQ-28): `GET /v1/hq/players/{playerId}` —
// the profile, whether the phone number and the email address are verified, how they sign in,
// whether a second factor is on, the devices they last signed in on, and the platform ban on record
// with its reason. Nothing of any organisation's dealings with them: those are not in the record.
//
// The ban and its lifting (`BanActions.tsx`) are offered only to a role holding `hq:players`, read
// from `GET /v1/me/access` at platform scope (the view carries no "may ban" field, by design); the
// server checks the permission again on every ban.

import type { HqPlayer, HqPlayerDevice } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Card, FactGrid, HeroStat, InfoTip, PageHero, PageSkeleton, Table, type TableColumn } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { problemCode, PROBLEM } from '../../api/problems';
import { queryKeys } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { usePlatformAccessHere } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_PLAYERS } from '../sections';
import { BanActions } from './BanActions';
import { fullName, isPlayerId, playerName } from './names';

/** `/players/$playerId`. */
export function PlayerPage(): ReactNode {
  const params = useParams({ strict: false }) as { playerId?: string };
  const playerId = params.playerId ?? '';
  // An id that is not a player's is answered here, as the server would answer it, without asking.
  return isPlayerId(playerId) ? <PlayerRecord playerId={playerId} /> : <NoSuchPlayer />;
}

function PlayerRecord({ playerId }: { readonly playerId: string }) {
  const { t } = useTranslation('hq');
  const api = useApi();
  const player = useQuery({
    queryKey: queryKeys.hqPlayer(playerId),
    queryFn: () => api.getHqPlayer({ path: { playerId } }),
    // Each read is a row in the platform's audit log: read again when asked, not on focus.
    refetchOnWindowFocus: false,
  });

  if (player.data === undefined) {
    const code = problemCode(player.error);
    if (code === PROBLEM.notFound || code === PROBLEM.validationFailed) return <NoSuchPlayer />;
    return player.isError ? (
      <div className="console-page">
        <BackToSearch />
        <LoadFailure error={player.error} message={t('players.record.unavailable')} onRetry={() => void player.refetch()} />
      </div>
    ) : (
      <PageSkeleton />
    );
  }
  return <PlayerView player={player.data} />;
}

function BackToSearch() {
  const { t } = useTranslation('hq');
  return (
    <p>
      <Link to={HQ_PATHS.players}>{t('players.record.back')}</Link>
    </p>
  );
}

function NoSuchPlayer() {
  const { t } = useTranslation('hq');
  return (
    <div className="console-page">
      <BackToSearch />
      <h1 className="console-page__title">{t('players.title')}</h1>
      <p role="alert">{t('players.record.noSuchPlayer')}</p>
    </div>
  );
}

function PlayerView({ player }: { readonly player: HqPlayer }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const access = usePlatformAccessHere();
  const [saved, setSaved] = useState<string | undefined>(undefined);
  const name = playerName(player);

  const given = (value: string | null) => (value === null || value === '' ? null : value);
  const profile = [
    { key: 'name', label: t('players.record.name'), value: fullName(player) },
    { key: 'displayName', label: t('players.record.displayName'), value: given(player.displayName) },
    { key: 'username', label: t('players.record.username'), value: player.username === null ? null : `@${player.username}` },
    { key: 'createdAt', label: t('players.record.createdAt'), value: format.dateTime(player.createdAt, { date: 'medium', time: 'short' }) },
  ];
  const notGiven = profile.filter((item) => item.value === null).map((item) => item.label.toLowerCase());
  const ban = player.platformBan;

  return (
    <div className="console-page">
      <PageHero
        breadcrumb={<Link to={HQ_PATHS.players}>{t('players.record.back')}</Link>}
        tileName={name}
        title={name}
        meta={
          <span className="console-hq-heading">
            <span className="console-hq-subject">{player.id}</span>
            <InfoTip>{t('players.record.audited')}</InfoTip>
          </span>
        }
        status={ban.banned ? <Badge tone="danger">{t('players.banned')}</Badge> : undefined}
        stats={
          <>
            <HeroStat label={t('players.record.methods')} value={player.signInMethods.length} />
            <HeroStat label={t('players.record.secondFactor')} value={player.twoFactorEnabled ? t('players.record.secondFactorOn') : t('players.record.secondFactorOff')} />
            <HeroStat label={t('players.devices.title')} value={player.devices.length} />
          </>
        }
      />
      {saved === undefined ? null : (
        <p className="console-hq-saved" role="status">
          {saved}
        </p>
      )}

      <div className="console-hq-record">
        <Card title={t('players.record.profile')} titleAs="h2">
          <FactGrid items={profile} columns={2} {...(notGiven.length === 0 ? {} : { missing: t('players.record.notGivenList', { what: notGiven.join(', ') }) })} />
        </Card>

        <Card title={t('players.record.contact')} titleAs="h2">
          <dl className="console-hq-facts">
            <Fact term={t('players.record.phone')}>
              <Verified value={player.phoneNumber} verified={player.phoneNumberVerified} />
            </Fact>
            <Fact term={t('players.record.email')}>
              <Verified value={player.email} verified={player.emailVerified} />
            </Fact>
          </dl>
        </Card>

        <Card title={t('players.record.signingIn')} titleAs="h2">
          <dl className="console-hq-facts">
            <Fact term={t('players.record.methods')}>
              {player.signInMethods.length === 0 ? (
                <NotGiven />
              ) : (
                <ul className="console-hq-tags">
                  {player.signInMethods.map((method) => (
                    <li key={method}>
                      <Badge>{t(`players.method.${method}`)}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Fact>
            <Fact term={t('players.record.secondFactor')}>
              {player.twoFactorEnabled ? t('players.record.secondFactorOn') : t('players.record.secondFactorOff')}
            </Fact>
          </dl>
        </Card>

        <Card title={t('players.ban.title')} titleAs="h2" className="console-hq-ban">
          <PlatformBanFacts player={player} />
          {access.permissions.has(HQ_PLAYERS) ? <BanActions player={player} name={name} onSaved={setSaved} /> : null}
        </Card>
      </div>

      <Devices devices={player.devices} />
    </div>
  );
}

function PlatformBanFacts({ player }: { readonly player: HqPlayer }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const ban = player.platformBan;
  if (!ban.banned && ban.reason === null) return <p>{t('players.ban.none')}</p>;
  return (
    <>
      <p className="console-hq-ban__state">
        {ban.banned ? <Badge tone="danger">{t('players.banned')}</Badge> : <Badge tone="warn">{t('players.ban.lapsedBadge')}</Badge>}
        <span>{ban.banned ? t('players.ban.inForce') : t('players.ban.lapsed')}</span>
      </p>
      <dl className="console-hq-facts">
        <Fact term={t('players.ban.reason')}>{ban.reason ?? <NotGiven />}</Fact>
        <Fact term={t('players.ban.until')}>
          {ban.expiresAt === null ? t('players.ban.untilLifted') : format.dateTime(ban.expiresAt, { date: 'medium', time: 'short' })}
        </Fact>
      </dl>
      <p className="console-muted">{t('players.ban.internal')}</p>
    </>
  );
}

function Devices({ devices }: { readonly devices: readonly HqPlayerDevice[] }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const columns = useMemo<readonly TableColumn<HqPlayerDevice>[]>(
    () => [
      { key: 'device', header: t('players.devices.device'), cell: (device) => <span className="console-hq-subject">{device.deviceKey}</span> },
      { key: 'first', header: t('players.devices.firstSeen'), cell: (device) => format.dateTime(device.firstSeenAt, { date: 'medium', time: 'short' }) },
      { key: 'last', header: t('players.devices.lastSeen'), cell: (device) => format.dateTime(device.lastSeenAt, { date: 'medium', time: 'short' }) },
    ],
    [t, format],
  );
  return (
    <section className="console-hq-devices" aria-labelledby="console-hq-devices-title">
      <h2 id="console-hq-devices-title">{t('players.devices.title')}</h2>
      <Table
        caption={t('players.devices.caption')}
        captionHidden
        columns={columns}
        rows={devices}
        rowKey={(device) => `${device.deviceKey} ${device.firstSeenAt}`}
        empty={t('players.devices.none')}
      />
    </section>
  );
}

function Fact({ term, children }: { readonly term: string; readonly children: ReactNode }) {
  return (
    <div className="console-hq-fact">
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function NotGiven() {
  const { t } = useTranslation('hq');
  return <span className="console-muted">{t('players.notGiven')}</span>;
}

/** A contact detail in full, and whether the person proved it is theirs. */
function Verified({ value, verified }: { readonly value: string | null; readonly verified: boolean }) {
  const { t } = useTranslation('hq');
  if (value === null) return <NotGiven />;
  return (
    <span className="console-hq-contact">
      <span className="console-hq-subject">{value}</span>
      {verified ? <Badge tone="ok">{t('players.record.verified')}</Badge> : <Badge tone="warn">{t('players.record.unverified')}</Badge>}
    </span>
  );
}
